/**
 * Thai Date and Time Utilities
 * ระบบจัดการวันที่และเวลาภาษาไทย
 */

// ชื่อเดือนภาษาไทย
export const THAI_MONTHS = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
]

// ชื่อเดือนภาษาไทยแบบสั้น
export const THAI_MONTHS_SHORT = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
]

// ชื่อวันภาษาไทย
export const THAI_DAYS = [
  'อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'
]

// ชื่อวันภาษาไทยแบบสั้น
export const THAI_DAYS_SHORT = [
  'อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.'
]

/**
 * แปลงปี ค.ศ. เป็น พ.ศ.
 */
export const toBuddhistYear = (year: number): number => {
  return year + 543
}

/**
 * แปลงปี พ.ศ. เป็น ค.ศ.
 */
export const toChristianYear = (buddhistYear: number): number => {
  return buddhistYear - 543
}

/**
 * จัดรูปแบบเวลาแบบ 24 ชั่วโมง
 */
export const formatTime24 = (date: Date): string => {
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return `${hours}:${minutes}`
}

/**
 * จัดรูปแบบเวลาแบบ 24 ชั่วโมงพร้อมวินาที
 */
export const formatTime24WithSeconds = (date: Date): string => {
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const seconds = date.getSeconds().toString().padStart(2, '0')
  return `${hours}:${minutes}:${seconds}`
}

/**
 * จัดรูปแบบวันที่ภาษาไทย
 * รูปแบบ: วันที่ DD เดือน YYYY
 */
export const formatThaiDate = (date: Date): string => {
  const day = date.getDate()
  const month = THAI_MONTHS[date.getMonth()]
  const year = toBuddhistYear(date.getFullYear())
  
  return `${day} ${month} ${year}`
}

/**
 * จัดรูปแบบวันที่ภาษาไทยแบบสั้น
 * รูปแบบ: DD/MM/YYYY (พ.ศ.)
 */
export const formatThaiDateShort = (date: Date): string => {
  // DEBUG: Log short date format
  console.log('DEBUG: formatThaiDateShort - Input date:', date)
  
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = toBuddhistYear(date.getFullYear())
  
  const result = `${day}/${month}/${year}`
  console.log('DEBUG: formatThaiDateShort - Output (numeric):', result)
  return result
}

/**
 * จัดรูปแบบวันที่ภาษาไทยพร้อมชื่อเดือนแบบย่อ
 * รูปแบบ: DD ธ.ค. YYYY (เช่น 23 ธ.ค. 2568)
 */
export const formatThaiDateWithMonthAbbreviation = (date: Date): string => {
  // DEBUG: Log month abbreviation format
  console.log('DEBUG: formatThaiDateWithMonthAbbreviation - Input date:', date)
  
  const day = date.getDate()
  const monthAbbrev = THAI_MONTHS_SHORT[date.getMonth()]
  const year = toBuddhistYear(date.getFullYear())
  
  const result = `${day} ${monthAbbrev} ${year}`
  console.log('DEBUG: formatThaiDateWithMonthAbbreviation - Output:', result)
  return result
}

/**
 * จัดรูปแบบวันที่และเวลาภาษาไทย
 * รูปแบบ: วันที่ DD เดือน YYYY เวลา HH:MM น.
 */
export const formatThaiDateTime = (date: Date): string => {
  const dateStr = formatThaiDate(date)
  const timeStr = formatTime24(date)
  
  return `${dateStr} เวลา ${timeStr} น.`
}

/**
 * จัดรูปแบบวันที่และเวลาภาษาไทยแบบสั้น
 * รูปแบบ: DD/MM/YYYY HH:MM
 */
export const formatThaiDateTimeShort = (date: Date): string => {
  const dateStr = formatThaiDateShort(date)
  const timeStr = formatTime24(date)
  
  return `${dateStr} ${timeStr}`
}

/**
 * จัดรูปแบบวันที่พร้อมชื่อวัน
 * รูปแบบ: วันจันทร์ที่ DD เดือน YYYY
 */
export const formatThaiDateWithDay = (date: Date): string => {
  const dayName = THAI_DAYS[date.getDay()]
  const day = date.getDate()
  const month = THAI_MONTHS[date.getMonth()]
  const year = toBuddhistYear(date.getFullYear())
  
  return `วัน${dayName}ที่ ${day} ${month} ${year}`
}

/**
 * จัดรูปแบบวันที่พร้อมชื่อวันแบบสั้น
 * รูปแบบ: จ. DD/MM/YYYY
 */
export const formatThaiDateWithDayShort = (date: Date): string => {
  // DEBUG: Log date with day short format
  console.log('DEBUG: formatThaiDateWithDayShort - Input date:', date)
  
  const dayName = THAI_DAYS_SHORT[date.getDay()]
  const dateStr = formatThaiDateShort(date)
  
  const result = `${dayName} ${dateStr}`
  console.log('DEBUG: formatThaiDateWithDayShort - Output:', result)
  return result
}

/**
 * จัดรูปแบบเดือนและปี
 * รูปแบบ: เดือน YYYY
 */
export const formatThaiMonthYear = (date: Date): string => {
  const month = THAI_MONTHS[date.getMonth()]
  const year = toBuddhistYear(date.getFullYear())
  
  return `${month} ${year}`
}

/**
 * จัดรูปแบบเดือนและปีแบบสั้น
 * รูปแบบ: MM/YYYY
 */
export const formatThaiMonthYearShort = (date: Date): string => {
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = toBuddhistYear(date.getFullYear())
  
  return `${month}/${year}`
}

/**
 * จัดรูปแบบช่วงเวลา
 * รูปแบบ: HH:MM - HH:MM น.
 */
export const formatTimeRange = (startTime: string, endTime: string): string => {
  return `${startTime} - ${endTime} น.`
}

/**
 * จัดรูปแบบช่วงวันที่
 * รูปแบบ: DD เดือน - DD เดือน YYYY
 */
export const formatThaiDateRange = (startDate: Date, endDate: Date): string => {
  const startDay = startDate.getDate()
  const endDay = endDate.getDate()
  const startMonth = THAI_MONTHS[startDate.getMonth()]
  const endMonth = THAI_MONTHS[endDate.getMonth()]
  const startYear = toBuddhistYear(startDate.getFullYear())
  const endYear = toBuddhistYear(endDate.getFullYear())
  
  // ถ้าเป็นปีเดียวกัน
  if (startYear === endYear) {
    // ถ้าเป็นเดือนเดียวกัน
    if (startMonth === endMonth) {
      return `${startDay} - ${endDay} ${startMonth} ${startYear}`
    } else {
      return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${startYear}`
    }
  } else {
    return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`
  }
}

/**
 * จัดรูปแบบช่วงวันที่แบบสั้น
 * รูปแบบ: DD-DD ธ.ค. YY
 */
export const formatThaiDateRangeShort = (startDate: Date, endDate: Date): string => {
  const startDay = startDate.getDate()
  const endDay = endDate.getDate()
  const startMonth = THAI_MONTHS_SHORT[startDate.getMonth()]
  const endMonth = THAI_MONTHS_SHORT[endDate.getMonth()]
  const startYear = toBuddhistYear(startDate.getFullYear()) % 100 // เอาแค่ 2 หลักท้าย
  const endYear = toBuddhistYear(endDate.getFullYear()) % 100
  
  // ถ้าเป็นปีเดียวกัน
  if (startYear === endYear) {
    // ถ้าเป็นเดือนเดียวกัน
    if (startMonth === endMonth) {
      return `${startDay}-${endDay} ${startMonth} ${startYear}`
    } else {
      return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${startYear}`
    }
  } else {
    return `${startDay} ${startMonth} ${startYear.toString().padStart(2, '0')} - ${endDay} ${endMonth} ${endYear.toString().padStart(2, '0')}`
  }
}

/**
 * แปลง string เป็น Date object
 */
export const parseThaiDate = (dateString: string): Date | null => {
  try {
    // รองรับรูปแบบ DD/MM/YYYY (พ.ศ.)
    const parts = dateString.split('/')
    if (parts.length === 3) {
      const day = parseInt(parts[0])
      const month = parseInt(parts[1]) - 1 // JavaScript month เริ่มจาก 0
      const year = toChristianYear(parseInt(parts[2]))
      
      return new Date(year, month, day)
    }
    
    // รองรับรูปแบบ ISO string
    return new Date(dateString)
  } catch (error) {
    console.error('Error parsing Thai date:', error)
    return null
  }
}

/**
 * ตรวจสอบว่าเป็นวันนี้หรือไม่
 */
export const isToday = (date: Date): boolean => {
  const today = new Date()
  return date.toDateString() === today.toDateString()
}

/**
 * ตรวจสอบว่าเป็นเมื่อวานหรือไม่
 */
export const isYesterday = (date: Date): boolean => {
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  return date.toDateString() === yesterday.toDateString()
}

/**
 * ตรวจสอบว่าเป็นพรุ่งนี้หรือไม่
 */
export const isTomorrow = (date: Date): boolean => {
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  return date.toDateString() === tomorrow.toDateString()
}

/**
 * จัดรูปแบบวันที่แบบสัมพัทธ์
 * รูปแบบ: วันนี้, เมื่อวาน, พรุ่งนี้, หรือวันที่ปกติ
 */
export const formatRelativeDate = (date: Date): string => {
  if (isToday(date)) {
    return 'วันนี้'
  } else if (isYesterday(date)) {
    return 'เมื่อวาน'
  } else if (isTomorrow(date)) {
    return 'พรุ่งนี้'
  } else {
    return formatThaiDate(date)
  }
}

/**
 * จัดรูปแบบวันที่และเวลาแบบสัมพัทธ์
 */
export const formatRelativeDateTime = (date: Date): string => {
  const relativeDate = formatRelativeDate(date)
  const time = formatTime24(date)
  
  if (relativeDate === 'วันนี้' || relativeDate === 'เมื่อวาน' || relativeDate === 'พรุ่งนี้') {
    return `${relativeDate} เวลา ${time} น.`
  } else {
    return formatThaiDateTime(date)
  }
}

/**
 * คำนวณอายุจากวันเกิด
 */
export const calculateAge = (birthDate: Date): number => {
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  
  return age
}

/**
 * จัดรูปแบบระยะเวลา (duration)
 */
export const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  
  if (hours > 0) {
    return `${hours} ชั่วโมง ${mins} นาที`
  } else {
    return `${mins} นาที`
  }
}

/**
 * ตรวจสอบว่าเป็นวันหยุดสุดสัปดาห์หรือไม่
 */
export const isWeekend = (date: Date): boolean => {
  const day = date.getDay()
  return day === 0 || day === 6 // อาทิตย์ = 0, เสาร์ = 6
}

/**
 * หาวันจันทร์ของสัปดาห์
 */
export const getMonday = (date: Date): Date => {
  const day = date.getDay()
  const diff = date.getDate() - day + (day === 0 ? -6 : 1) // ปรับสำหรับอาทิตย์
  return new Date(date.setDate(diff))
}

/**
 * หาวันอาทิตย์ของสัปดาห์
 */
export const getSunday = (date: Date): Date => {
  const day = date.getDay()
  const diff = date.getDate() - day
  return new Date(date.setDate(diff))
}

/**
 * สร้าง Date object สำหรับเวลาปัจจุบัน
 */
export const now = (): Date => {
  return new Date()
}

/**
 * สร้าง Date object สำหรับวันนี้เวลา 00:00:00
 */
export const today = (): Date => {
  const date = new Date()
  date.setHours(0, 0, 0, 0)
  return date
}

/**
 * สร้าง Date object สำหรับพรุ่งนี้เวลา 00:00:00
 */
export const tomorrow = (): Date => {
  const date = today()
  date.setDate(date.getDate() + 1)
  return date
}

/**
 * สร้าง Date object สำหรับเมื่อวานเวลา 00:00:00
 */
export const yesterday = (): Date => {
  const date = today()
  date.setDate(date.getDate() - 1)
  return date
}

// Export default object with all functions
export default {
  THAI_MONTHS,
  THAI_MONTHS_SHORT,
  THAI_DAYS,
  THAI_DAYS_SHORT,
  toBuddhistYear,
  toChristianYear,
  formatTime24,
  formatTime24WithSeconds,
  formatThaiDate,
  formatThaiDateShort,
  formatThaiDateTime,
  formatThaiDateTimeShort,
  formatThaiDateWithDay,
  formatThaiDateWithDayShort,
  formatThaiDateWithMonthAbbreviation,
  formatThaiMonthYear,
  formatThaiMonthYearShort,
  formatTimeRange,
  formatThaiDateRange,
  formatThaiDateRangeShort,
  parseThaiDate,
  isToday,
  isYesterday,
  isTomorrow,
  formatRelativeDate,
  formatRelativeDateTime,
  calculateAge,
  formatDuration,
  isWeekend,
  getMonday,
  getSunday,
  now,
  today,
  tomorrow,
  yesterday
}